/**
 * Bring a spreadsheet of units in at once.
 *
 * A hospital onboarding has its unit list already — in a spreadsheet kept by
 * nursing administration — and typing thirty units into a form is how a
 * product gets abandoned on day one. The backend parses the CSV; this reads
 * the file, says what columns it expects, and shows what happened per row.
 */

import { useId, useState } from 'react';
import { FileDrop } from '../../shared/FileDrop';
import { isCallFailure } from '../../lib/callable';
import { SCOPE_CSV_MAX } from '../../types/fieldLimits';
import { formatBytes } from '../policies/useUpload';
import { useImportScopes, type ImportResult } from './useScopes';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './ScopeOfService.module.css';

/** The header row the backend recognises. unitName and unitCode are required. */
export const CSV_COLUMNS = [
  ['unitName', 'required'],
  ['unitCode', 'required, unique'],
  ['departmentId', 'a department id from Taxonomy'],
  ['description', ''],
  ['patientPopulations', 'separate entries with ;'],
  ['servicesProvided', 'separate entries with ;'],
  ['exclusionsLimitations', ''],
  ['hoursOfOperation', ''],
  ['staffingRequirements', ''],
  ['equipmentRequirements', ''],
  ['effectiveDate', 'YYYY-MM-DD'],
  ['nextReviewDate', 'YYYY-MM-DD'],
] as const;

export const TEMPLATE_CSV =
  CSV_COLUMNS.map(([c]) => c).join(',') +
  '\nEmergency Department,ED-001,,Level II trauma center,Adults;Pediatrics,Emergency care;Triage,,24/7,,,2026-01-01,2027-01-01\n';

export const TOO_BIG_MESSAGE = `That file is larger than ${formatBytes(SCOPE_CSV_MAX)}. Split it into smaller files.`;

/** Rows in a CSV, not counting the header. Good enough for a "12 rows" label. */
export function countRows(text: string): number {
  return Math.max(0, text.split(/\r?\n/).filter((l) => l.trim()).length - 1);
}

export function ImportScopesPanel({ orgId, onClose }: { orgId: string | null; onClose: () => void }) {
  const importScopes = useImportScopes(orgId);
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const hintId = useId();

  async function onFile(f: File) {
    setProblem(null);
    setText(null);
    importScopes.reset();
    if (f.size > SCOPE_CSV_MAX) {
      setFile(null);
      setProblem(TOO_BIG_MESSAGE);
      return;
    }
    setFile(f);
    try {
      setText(await f.text());
    } catch {
      setFile(null);
      setProblem('That file could not be read.');
    }
  }

  const result: ImportResult | undefined = importScopes.data;
  const error = importScopes.error;

  return (
    <section className={own.panel} aria-labelledby="import-title">
      <h2 className={own.panelTitle} id="import-title">Import units from a CSV</h2>
      <p className={styles.subtitle} style={{ marginTop: 0 }}>
        One row per unit, with a header row. Every imported unit starts as a draft.
      </p>
      <ul className={own.columns}>
        {CSV_COLUMNS.map(([col, note]) => (
          <li key={col}>
            <code>{col}</code>
            {note && <span className={own.muted}> — {note}</span>}
          </li>
        ))}
      </ul>
      <p className={styles.subtitle}>
        <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE_CSV)}`} download="scope-of-service-template.csv">
          Download a template
        </a>{' '}
        with these columns and one example row.
      </p>

      <div className={form.field} style={{ marginTop: 'var(--sp-4)', marginBottom: 0 }}>
        <label className={form.label} htmlFor="scope-csv">File</label>
        <FileDrop
          id="scope-csv"
          onFile={(f) => void onFile(f)}
          accept=".csv,text/csv"
          prompt="Choose a CSV"
          describedBy={hintId}
          invalid={Boolean(problem)}
          disabled={importScopes.isPending}
        />
        <p className={styles.subtitle} id={hintId} style={{ fontSize: 'var(--fs-meta)' }}>
          CSV only, up to {formatBytes(SCOPE_CSV_MAX)}.
        </p>
      </div>

      {problem && (
        <p className={own.selected} role="alert">{problem}</p>
      )}
      {file && text !== null && !result && (
        <p className={own.selected} role="status">
          Selected: <b>{file.name}</b> ({formatBytes(file.size)}) — {countRows(text)}{' '}
          {countRows(text) === 1 ? 'row' : 'rows'}.
        </p>
      )}

      {error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert" style={{ marginTop: 'var(--sp-4)' }}>
          {isCallFailure(error) ? error.message : 'The import could not be run.'}
        </div>
      )}

      {result && (
        <div className={own.results} role="status">
          <p className={own.what}>
            <b>{result.created.length}</b> {result.created.length === 1 ? 'unit' : 'units'} imported
            {result.failed.length > 0 && (
              <>, <b>{result.failed.length}</b> {result.failed.length === 1 ? 'row' : 'rows'} skipped</>
            )}.
          </p>
          {result.failed.length > 0 && (
            <ul>
              {result.failed.map((f) => (
                <li key={f.row}>
                  Row {f.row} ({f.unitCode}): {f.errors.join('; ')}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className={`${styles.actions} ${own.actions}`}>
        {!result && (
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={!text || importScopes.isPending}
            onClick={() => text && importScopes.mutate(text)}
          >
            {importScopes.isPending ? 'Importing…' : `Import ${text ? countRows(text) : ''} ${text && countRows(text) === 1 ? 'unit' : 'units'}`.replace(/\s+/g, ' ')}
          </button>
        )}
        <button type="button" className={styles.btn} onClick={onClose}>
          {result ? 'Done' : 'Cancel'}
        </button>
      </div>
    </section>
  );
}
